import { useEffect, useState } from "react";
import styled from "styled-components";
import theme from "../../../../../UI/theme";
import Button from "../../../../UiComponents/Button";
import Modal from "../../../../UiComponents/Modal";
import CheckboxField from "../../../fields/CheckBoxField";
import NumberField from "../../../fields/NumberField";
import StringField from "../../../fields/StringField";
import { DisplayField, editableColor, readOnlyColor, TableCell } from "../../../fields/Styles";
import { FieldButton } from "../../../fields/styling/FieldStyles";
import { HeaderRow, StyledTable } from "../../../table/styles/TableStyles";
import { cellStates } from "../../../table/tableTypes";
import { displayModes } from "../../../utils/installationTypes";
import { ButtonRowContainer, ModalContainer, ModalHeader } from "../../../../panel/ModalStyles";
import { sourceMatrixTypes } from "../../sourceMatrix/sourceMatrixTypes";
import { routingModes } from "../routingModes";

const FieldContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

const RoutePreview = styled.div`
    margin: 10px 0;
    padding: 8px;
    background-color: ${theme.colors.darkSpace90};
    color: ${theme.colors.textGray};
    font-size: ${theme.fontSize.small};
    border-radius: 2px;
`

const RoutePreviewLine = styled.div`
    margin-top: 4px;
`

const SourceConfigField = ({ sourceConfig, updateSourceConfig, sources, sourceMatrix, routingMode, routingModeDisplay }) => {
    const [showModal, setShowModal] = useState(false);
    const isFlexMatrix = sourceMatrix?.type === sourceMatrixTypes.FLEX;


    const updateMapping = (sourceId, address) => {
        if (!isNaN(parseInt(address))) {
            const updatedMapping = { ...sourceConfig?.mapping, [sourceId]: parseInt(address) };
            updateSourceConfig({ ...sourceConfig, mapping: updatedMapping });
        } else if (!address) {
            removeMapping(sourceId);
        }
    }

    const removeMapping = (sourceId) => {
        const updatedMapping = { ...sourceConfig?.mapping };
        delete updatedMapping[sourceId];
        updateSourceConfig({ ...sourceConfig, mapping: updatedMapping });
    }

    const setAvailability = (sourceId, state) => {
        let updatedUnavailableSources = sourceConfig?.unavailableSources ? [...sourceConfig.unavailableSources] : [];
        if (state && !updatedUnavailableSources.includes(sourceId)) {
            updatedUnavailableSources.push(sourceId);
        } else {
            updatedUnavailableSources = updatedUnavailableSources.filter(id => id !== sourceId);
        }
        updateSourceConfig({ ...sourceConfig, unavailableSources: updatedUnavailableSources });
    }

    const updateFlexOutputs = (outputs) => {
        updateSourceConfig({
            ...sourceConfig,
            flex: {
                ...sourceConfig?.flex,
                outputs
            }
        });
    }

    const resetToDefault = () => {
        updateSourceConfig(null);
    }

    const hasFlexConfig = sourceConfig?.flex?.outputs;
    const hasMappingConfig = !isFlexMatrix && sourceConfig?.mapping && Object.keys(sourceConfig.mapping).length;
    const isConfigured = sourceConfig && (hasMappingConfig || sourceConfig?.unavailableSources?.length || hasFlexConfig);
    return <>
        <FieldContainer>

            <div style={{ color: isConfigured ? editableColor : readOnlyColor }}>{isConfigured ? 'Configured' : 'Default'}</div>
            <div>
                <FieldButton tertiary small onClick={() => setShowModal(true)}>Edit</FieldButton>
            </div>
        </FieldContainer>
        <Modal show={showModal} close={() => setShowModal(false)}>
            <ModalContainer>
                <ModalHeader>Source Config</ModalHeader>
                <ConfigEditor
                    mapping={sourceConfig?.mapping}
                    updateMapping={updateMapping}
                    unavailableSources={sourceConfig?.unavailableSources}
                    setAvailability={setAvailability}
                    sources={sources}
                    closeModal={() => setShowModal(false)}
                    isConfigured={isConfigured}
                    resetToDefault={resetToDefault}
                    isFlexMatrix={isFlexMatrix}
                    flex={sourceConfig?.flex}
                    updateFlexOutputs={updateFlexOutputs}
                    routingMode={routingMode}
                    routingModeDisplay={routingModeDisplay}
                />
            </ModalContainer>
        </Modal>
    </>
}

export default SourceConfigField;

const Container = styled.div`
    @media only screen and (max-width: ${theme.screenSizes.small - 1}px) {
        width: 100%
    }
    @media only screen and (min-width: ${theme.screenSizes.small}px) {
        min-width: 30em;
    }

    >table {
        margin: 10px 0;
    }
    >div>button  {
        margin-right: 12px;
    }
`

const ConfigEditor = ({ mapping, updateMapping, unavailableSources, setAvailability, sources, closeModal, isConfigured, resetToDefault, isFlexMatrix, flex, updateFlexOutputs, routingMode, routingModeDisplay }) => {

    // For flex matrixes the source must declare which physical inputs it occupies
    // via its `inputs` array. Sources without that prop can't be routed by a flex
    // matrix, so hide them from the available-sources list.
    const displaySources = isFlexMatrix
        ? sources.filter(source => Array.isArray(source.inputs) && source.inputs.length > 0)
        : sources;

    return <Container>
        {isFlexMatrix ? <>
            <FieldContainer>
                <div>Flex outputs</div>
                <FlexNumberListField
                    value={flex?.outputs}
                    setValue={updateFlexOutputs}
                    cellState={cellStates.FORM}
                />
            </FieldContainer>
            <FlexRoutePreview
                sources={displaySources.filter(source => !(unavailableSources && unavailableSources.includes(source.sourceId)))}
                outputs={flex?.outputs}
                routingMode={routingMode}
                routingModeDisplay={routingModeDisplay}
            />
        </> : <></>}
        <StyledTable borderColor={theme.colors.dropdownGray}>
            <thead>
                <HeaderRow backgroundColor={theme.colors.dropdownGray}>
                    <th>Source</th>
                    <th>{isFlexMatrix ? 'Inputs' : 'Address'}</th>
                    {!isFlexMatrix ? <th>Mapped Address</th> : <></>}
                    <th>Available</th>
                </HeaderRow>
            </thead>
            <tbody>
                {displaySources.map(source => {
                    const isUnavailable = (unavailableSources && unavailableSources.includes(source.sourceId))
                    const cellState = cellStates.FORM;
                    return <tr key={source.sourceId}>
                        <TableCell>{source.name}</TableCell>
                        <TableCell style={{ width: isFlexMatrix ? '100px' : '50px' }}>
                            <DisplayField>{isFlexMatrix ? formatSourceInputs(source) : source.address}</DisplayField>
                        </TableCell>
                        {!isFlexMatrix ? <TableCell style={{ width: '50px' }}>
                            <NumberField
                                value={mapping ? mapping[source.sourceId] : ''}
                                setValue={(value) => updateMapping(source.sourceId, value)}
                                cellState={cellState}
                            />
                        </TableCell> : <></>}
                        <TableCell>
                            <CheckboxField
                                checked={!isUnavailable}
                                setValue={() => setAvailability(source.sourceId, !isUnavailable)}
                                cellControl={{ cellState, displayMode: displayModes.TABLE }}
                            />
                        </TableCell>
                    </tr>
                })}
            </tbody>
        </StyledTable>
        <ButtonRowContainer>
            <Button primary onClick={closeModal}>Ok</Button>
            {isConfigured ? <>
                <Button secondary onClick={resetToDefault}>Reset to default</Button>
            </> : <></>}
        </ButtonRowContainer>
    </Container>
}

const FlexRoutePreview = ({ sources, outputs, routingMode, routingModeDisplay }) => {
    const normalizedOutputs = parseNumberList(outputs);

    if (!normalizedOutputs.length || !sources.length) return <></>;

    const previewSources = sources.filter(source => parseNumberList(source.inputs).length > 0);
    if (!previewSources.length) return <></>;

    return <RoutePreview>
        <div>Routing mode: {routingModeDisplay}</div>
        {previewSources.slice(0, 3).map(source => {
            const inputs = parseNumberList(source.inputs);
            const routes = getRoutePreview(inputs, normalizedOutputs, routingMode);
            const mismatch = routingMode === routingModes.PAIRED && inputs.length !== normalizedOutputs.length;
            return <RoutePreviewLine key={source.sourceId}>
                {source.name}: {mismatch ? 'Paired requires equal input/output counts' : routes.join(', ')}
            </RoutePreviewLine>
        })}
        {previewSources.length > 3 ? <RoutePreviewLine>…and {previewSources.length - 3} more</RoutePreviewLine> : <></>}
    </RoutePreview>
}

const getRoutePreview = (inputs, outputs, routingMode) => {
    if (routingMode === routingModes.PAIRED) {
        return inputs.map((input, index) => `${input} → ${outputs[index]}`);
    }

    return outputs.flatMap(output => inputs.map(input => `${input} → ${output}`));
}

const parseNumberList = (value) => {
    if (Array.isArray(value)) return value.map(n => parseInt(n)).filter(n => !isNaN(n));
    if (typeof value === 'string') return value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
    return [];
}

const FlexNumberListField = ({ value, setValue, cellState }) => {
    const [text, setText] = useState(formatNumberList(value));

    useEffect(() => {
        setText(formatNumberList(value));
    }, [value]);

    const onChange = (updatedText) => {
        setText(updatedText);
        setValue(updatedText);
    }

    return <StringField
        value={text}
        setValue={onChange}
        cellState={cellState}
        placeholder='1, 2, 3'
    />
}

const formatNumberList = (value) => {
    return Array.isArray(value) ? value.join(', ') : value || '';
}

const formatSourceInputs = (source) => {
    return Array.isArray(source.inputs) && source.inputs.length ? source.inputs.join(', ') : source.address;
}
